import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { addWeeks, format, startOfWeek } from 'date-fns';
import { es } from 'date-fns/locale';
import { Thermometer, AlertTriangle } from 'lucide-react';
import axios from '../api/axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const WEEKS = 12;

const parseDate = (str) => {
  if (!str) return null;
  const [d, m, y] = str.split('/');
  return new Date(parseInt(y, 10), parseInt(m, 10) - 1, parseInt(d, 10));
};

const barColor = (pct) => (pct > 100 ? 'bg-red-600' : pct >= 85 ? 'bg-amber-500' : 'bg-emerald-600');

const OcupacionBar = ({ cam }) => {
  const pct = cam.capacidad ? Math.round((cam.ocupado / cam.capacidad) * 100) : 0;
  return (
    <div className="space-y-1" data-testid={`camara-bar-${cam.id}`}>
      <div className="flex justify-between text-sm">
        <span data-testid={`camara-ocupado-${cam.id}`}>{cam.ocupado} / {cam.capacidad} unidades</span>
        <span className={pct > 100 ? 'text-red-600 font-semibold' : 'text-gray-600'}>{pct}%</span>
      </div>
      <div className="h-3 w-full bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full ${barColor(pct)} transition-[width] duration-500`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <p className="text-xs text-gray-500" data-testid={`camara-libre-${cam.id}`}>
        {cam.libre >= 0 ? `${cam.libre} libres` : `Excedida en ${-cam.libre}`}
      </p>
    </div>
  );
};

const MuestrasList = ({ muestras, onOpen }) => {
  const activas = muestras.filter((m) => m.activa);
  if (!activas.length) return <p className="text-sm text-gray-500">Sin muestras dentro.</p>;
  return (
    <div className="divide-y border rounded-md">
      {activas.map((m) => (
        <button key={m.id} type="button" onClick={() => onOpen(m.id)} className="w-full text-left px-3 py-2 hover:bg-gray-50 flex justify-between gap-2 text-sm" data-testid={`camara-muestra-${m.id}`}>
          <span className="truncate"><strong>{m.product}</strong> · {m.client}{m.num_ensayo ? ` · ${m.num_ensayo}` : ''}</span>
          <span className="whitespace-nowrap text-gray-600">{m.cantidad} ud · sale {m.fecha_liberacion || 'sin fecha'}</span>
        </button>
      ))}
    </div>
  );
};

const Prevision = ({ camaras }) => {
  const weeks = useMemo(
    () => Array.from({ length: WEEKS }, (_, i) => addWeeks(startOfWeek(new Date(), { weekStartsOn: 1 }), i)),
    []
  );
  const usedAt = (cam, day) =>
    cam.muestras.filter((m) => m.activa && (!m.fecha_liberacion || parseDate(m.fecha_liberacion) >= day)).reduce((a, m) => a + m.cantidad, 0);
  return (
    <Card>
      <CardHeader><CardTitle className="text-lg">Ocupación prevista (próximas {WEEKS} semanas)</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="prevision-table">
          <thead>
            <tr>
              <th className="text-left p-2">Cámara</th>
              {weeks.map((w) => <th key={w.toISOString()} className="p-2 font-medium text-gray-600 whitespace-nowrap">{format(w, 'dd MMM', { locale: es })}</th>)}
            </tr>
          </thead>
          <tbody>
            {camaras.map((cam) => (
              <tr key={cam.id} className="border-t">
                <td className="p-2 font-medium whitespace-nowrap">{cam.name}</td>
                {weeks.map((w) => {
                  const used = usedAt(cam, w);
                  const pct = cam.capacidad ? (used / cam.capacidad) * 100 : 0;
                  return (
                    <td key={w.toISOString()} className="p-1 text-center">
                      <div className={`rounded px-1 py-1 text-white text-xs ${barColor(pct)}`} title={`${used}/${cam.capacidad}`}>
                        {cam.capacidad - used}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <p className="text-xs text-gray-500 mt-2">Huecos libres al inicio de cada semana, según la última fecha de Salida Cámara de cada muestra.</p>
      </CardContent>
    </Card>
  );
};

export default function Camaras() {
  const navigate = useNavigate();
  const [camaras, setCamaras] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get('/camaras/ocupacion')
      .then((r) => setCamaras(r.data))
      .catch(() => toast.error('Error al cargar las cámaras'))
      .finally(() => setLoading(false));
  }, []);

  const openSample = async (id) => {
    const res = await axios.get(`/work-samples/${id}`);
    navigate('/entrada-datos', { state: { editSample: res.data, returnTo: 'camaras' } });
  };

  if (loading) return <div className="p-8 text-gray-500">Cargando...</div>;

  return (
    <div className="space-y-6" data-testid="camaras-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2"><Thermometer /> Cámaras</h1>
        <p className="text-gray-600 mt-1">Ocupación de las cámaras climáticas con muestras de Estabilidades</p>
      </div>
      {camaras.length === 0 ? (
        <p className="text-gray-500" data-testid="camaras-empty">No hay cámaras. Créalas en Configuración &gt; Cámaras.</p>
      ) : (
        <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {camaras.map((cam) => (
              <Card key={cam.id} data-testid={`camara-card-${cam.id}`}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center justify-between">
                    {cam.name}
                    {cam.libre < 0 && <Badge variant="destructive" className="flex items-center gap-1"><AlertTriangle size={12} /> Excedida</Badge>}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <OcupacionBar cam={cam} />
                  <MuestrasList muestras={cam.muestras} onOpen={openSample} />
                </CardContent>
              </Card>
            ))}
          </div>
          <Prevision camaras={camaras} />
        </>
      )}
    </div>
  );
}
